/**
 * cart-core.js
 * Shared cart utilities: AJAX helpers, event bus, undo-removal toast,
 * low-stock detection, and the session/persona accessor used by other modules.
 *
 * Global: window.CartCore (assigned at bottom)
 */
(function () {
  'use strict';

  /* ── Constants ─────────────────────────────────────────────────────────────── */
  const LOW_STOCK_THRESHOLD = 5;
  const TOAST_DURATION = 5000; // ms
  const UNDO_HOLD_MS = 4800;

  /* ── Internal state ─────────────────────────────────────────────────────────── */
  let _undoTimer = null;
  let _pendingUndo = null; // { key, qty, productTitle }
  let _toastEl = null;

  /* ── Helpers ────────────────────────────────────────────────────────────────── */
  const fetchJSON = (url, opts) =>
    fetch(url, { credentials: 'same-origin', ...opts }).then(r => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    });

  const postJSON = (url, body) =>
    fetchJSON(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

  /* ── Event Bus ──────────────────────────────────────────────────────────────── */
  /**
   * Fires a custom event on document.
   * Internal name: 'cu:cart:updated'
   * Also fires the legacy T4S events so the theme JS keeps working.
   */
  const emit = (name, detail = {}) => {
    document.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }));
  };

  const emitCartUpdated = (cart) => {
    emit('cu:cart:updated', { cart });
    // legacy / T4S compat
    emit('cart:updated', { cart });
    emit('cart:refresh', { cart });
    emit('t4s:cart:updated', { cart });
    emit('t4s:cart_updated', { cart });
  };

  /* ── Cart API ───────────────────────────────────────────────────────────────── */
  const getCart = () => fetchJSON('/cart.js');

  const addItems = (items) =>
    postJSON('/cart/add.js', { items }).then(() => getCart()).then(cart => {
      emitCartUpdated(cart);
      return cart;
    });

  const changeItem = (id, quantity) =>
    postJSON('/cart/change.js', { id, quantity }).then(cart => {
      emitCartUpdated(cart);
      return cart;
    });

  const updateNote = (note) =>
    postJSON('/cart/update.js', { note }).then(cart => {
      emitCartUpdated(cart);
      return cart;
    });

  /* ── Shipping threshold ─────────────────────────────────────────────────────── */
  /**
   * Returns { pct, remaining, reached, threshold } from the T4S settings
   * already rendered in the page as data attributes, or from window.CartCore.config.
   */
  const getShipStatus = (totalCents) => {
    const cfg = (window.CartCore && window.CartCore.config) || {};
    const threshold = cfg.freeShipThreshold || 0; // cents
    if (threshold <= 0) return { pct: 100, remaining: 0, reached: true, threshold: 0 };
    const pct = Math.min(100, Math.round((totalCents / threshold) * 100));
    const remaining = Math.max(0, threshold - totalCents);
    return { pct, remaining, reached: totalCents >= threshold, threshold };
  };

  /* ── Persona helpers ────────────────────────────────────────────────────────── */
  const personaLabels = {
    pro_barista: 'Für Profis',
    filter_enthusiast: 'Für Filter-Fans',
    home_medium: 'Für Home-Baristas',
    home_beginner: 'Für Einsteiger',
    outdoor: 'Für unterwegs',
    vanlife: 'Für Vanlife',
    general: 'Empfohlen',
  };

  const getPersona = () => ({
    persona: localStorage.getItem('brewout_persona') || '',
    confidence: parseFloat(localStorage.getItem('brewout_persona_confidence') || '0'),
    scores: localStorage.getItem('brewout_persona_scores') || '',
    sessionId: localStorage.getItem('brewout_session_id') || '',
    label: personaLabels[localStorage.getItem('brewout_persona')] || '',
  });

  /* ── Money formatter ─────────────────────────────────────────────────────────── */
  const money = (() => {
    // Use locale + currency injected by Liquid on first call
    let fmt;
    return (cents) => {
      if (!fmt) {
        const el = document.querySelector('[data-cu-currency]');
        const locale = (el && el.dataset.cuLocale) || navigator.language;
        const currency = (el && el.dataset.cuCurrency) || 'EUR';
        fmt = new Intl.NumberFormat(locale, { style: 'currency', currency });
      }
      return fmt.format(cents / 100);
    };
  })();

  /* ── Undo-removal Toast ─────────────────────────────────────────────────────── */
  const ensureToast = () => {
    if (_toastEl) return _toastEl;
    _toastEl = document.createElement('div');
    _toastEl.className = 'cu-toast';
    _toastEl.setAttribute('role', 'status');
    _toastEl.setAttribute('aria-live', 'polite');
    _toastEl.innerHTML = `
      <span class="cu-toast__msg"></span>
      <button type="button" class="cu-toast__undo">Rückgängig</button>
    `;
    _toastEl.querySelector('.cu-toast__undo').addEventListener('click', () => {
      if (!_pendingUndo) return;
      const { key, qty } = _pendingUndo;
      clearTimeout(_undoTimer);
      hideToast();
      changeItem(key, qty);
    });
    document.body.appendChild(_toastEl);
    return _toastEl;
  };

  const showToast = (title, undoData) => {
    const toast = ensureToast();
    toast.querySelector('.cu-toast__msg').textContent =
      `"${title}" entfernt`;
    _pendingUndo = undoData;
    clearTimeout(_undoTimer);
    toast.classList.add('is--visible');
    _undoTimer = setTimeout(hideToast, TOAST_DURATION);
  };

  const hideToast = () => {
    if (!_toastEl) return;
    _toastEl.classList.remove('is--visible');
    _pendingUndo = null;
  };

  /* ── Remove with undo ────────────────────────────────────────────────────────── */
  /**
   * Removes an item and shows undo toast.
   * @param {string} key  — Shopify line item key
   * @param {number} qty  — Current qty (to restore)
   * @param {string} title — Product title for toast message
   * @param {HTMLElement} [itemEl] — DOM element to animate out
   */
  const removeItemWithUndo = (key, qty, title, itemEl) => {
    // Animate out immediately for optimistic UI
    if (itemEl) {
      itemEl.style.transition = 'opacity 0.25s ease, transform 0.25s ease, max-height 0.3s ease';
      itemEl.style.opacity = '0';
      itemEl.style.transform = 'translateX(16px)';
    }

    showToast(title, { key, qty });

    // After brief delay fire the actual API call, giving undo time to cancel
    let cancelled = false;
    const prev = _pendingUndo;
    _undoTimer = setTimeout(async () => {
      // If the undo button was hit, _pendingUndo will be null or different
      if (!_pendingUndo || _pendingUndo.key !== key) return;
      try {
        await changeItem(key, 0);
      } catch (e) {
        // Restore on error
        if (itemEl) {
          itemEl.style.opacity = '';
          itemEl.style.transform = '';
        }
      }
    }, UNDO_HOLD_MS);
  };

  /* ── Low-stock helper ────────────────────────────────────────────────────────── */
  const getLowStockItems = (cartItems) =>
    cartItems.filter(item => {
      const mgmt = item.variant && item.variant.inventory_management;
      const policy = item.variant && item.variant.inventory_policy;
      const qty = item.variant && item.variant.inventory_quantity;
      return mgmt && mgmt !== '' && policy !== 'continue' && qty > 0 && qty <= LOW_STOCK_THRESHOLD;
    });

  /* ── Quantity stepper binding ────────────────────────────────────────────────── */
  /**
   * Attaches qty stepper events to any `.cu-qty` elements within `container`.
   * Expects sibling data attributes: [data-cu-item-key], [data-cu-item-min], [data-cu-item-max]
   */
  const bindQtySteppers = (container) => {
    container.querySelectorAll('.cu-qty').forEach(stepper => {
      const input = stepper.querySelector('.cu-qty__input');
      if (!input || stepper._cuBound) return;
      stepper._cuBound = true;

      const getKey = () => input.dataset.id || input.name;
      const min = parseInt(input.min || '1');
      const max = parseInt(input.max || '9999');

      stepper.querySelector('[data-cu-decrease]').addEventListener('click', () => {
        const current = parseInt(input.value);
        if (current <= min) return; // let remove handle this
        input.value = current - 1;
        changeItem(getKey(), current - 1);
      });

      stepper.querySelector('[data-cu-increase]').addEventListener('click', () => {
        const current = parseInt(input.value);
        if (current >= max) return;
        input.value = current + 1;
        changeItem(getKey(), current + 1);
      });

      let debounceTimer;
      input.addEventListener('change', () => {
        clearTimeout(debounceTimer);
        const val = Math.max(min, Math.min(max, parseInt(input.value) || min));
        input.value = val;
        debounceTimer = setTimeout(() => changeItem(getKey(), val), 400);
      });
    });
  };

  /* ── Remove button binding ───────────────────────────────────────────────────── */
  const bindRemoveButtons = (container) => {
    container.querySelectorAll('[data-cu-remove]').forEach(btn => {
      if (btn._cuBound) return;
      btn._cuBound = true;
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const key = btn.dataset.cuRemove || btn.closest('[data-cu-item-key]')?.dataset.cuItemKey;
        const qty = parseInt(btn.dataset.cuRemoveQty || '1');
        const title = btn.dataset.cuRemoveTitle || 'Artikel';
        const itemEl = btn.closest('.cu-item');
        removeItemWithUndo(key, qty, title, itemEl);
      });
    });
  };

  /* ── Public API ─────────────────────────────────────────────────────────────── */
  window.CartCore = {
    // Config populated by Liquid-rendered data attributes
    config: {},

    // Helpers
    fetchJSON,
    postJSON,
    money,
    getCart,
    addItems,
    changeItem,
    updateNote,
    emitCartUpdated,

    // Shipping
    getShipStatus,

    // Persona
    getPersona,
    personaLabels,

    // Undo toast
    removeItemWithUndo,
    showToast,
    hideToast,

    // Stock
    getLowStockItems,
    LOW_STOCK_THRESHOLD,

    // Stepper / remove binding
    bindQtySteppers,
    bindRemoveButtons,

    // Event subscription shorthands
    on: (name, fn) => document.addEventListener(name, fn),
    off: (name, fn) => document.removeEventListener(name, fn),
    emit,
  };

  /* ── Init: read config from DOM ──────────────────────────────────────────────── */
  document.addEventListener('DOMContentLoaded', () => {
    const cfg = document.querySelector('[data-cu-config]');
    if (cfg && cfg.dataset.cuConfig) {
      try {
        Object.assign(window.CartCore.config, JSON.parse(cfg.dataset.cuConfig));
      } catch (e) {/* ignore */}
    }
  });
})();
